import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getSupabaseClient } from '@/utils/supabase/client';

interface AuthContextType {
  user: any | null;
  loading: boolean;
  accessToken: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Track if we've already warned about missing provider
let hasWarnedAboutProvider = false;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  useEffect(() => {
    const supabase = getSupabaseClient();
    
    // Check for existing session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        // If there's an error (e.g., invalid refresh token), clear the session
        console.error('Session error, clearing auth state:', error.message);
        supabase.auth.signOut(); // Clear invalid session
        setUser(null);
        setAccessToken(null);
      } else {
        setUser(session?.user ?? null);
        setAccessToken(session?.access_token ?? null);
      }
      setLoading(false);
    }).catch((err) => {
      console.error('Failed to get session:', err);
      setUser(null);
      setAccessToken(null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setAccessToken(session?.access_token ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const supabase = getSupabaseClient();
    console.log('AuthContext signIn - Starting login for:', email);
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    console.log('AuthContext signIn - Response:', { 
      hasData: !!data, 
      hasSession: !!data?.session, 
      hasUser: !!data?.user,
      hasAccessToken: !!data?.session?.access_token,
      tokenPreview: data?.session?.access_token?.substring(0, 30) + '...',
      error: error?.message 
    });

    if (error) throw error;
    
    setUser(data.user);
    setAccessToken(data.session.access_token);
    
    console.log('AuthContext signIn - Login successful, token stored');
  };

  const signOut = async () => {
    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    
    setUser(null);
    setAccessToken(null);
  };

  const getAccessToken = async () => {
    const supabase = getSupabaseClient();
    // Try to refresh the session to get a fresh token
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error || !session) {
      console.error('Failed to get session:', error);
      return null;
    }
    
    // Update the stored access token if it changed
    if (session.access_token !== accessToken) {
      setAccessToken(session.access_token);
    }
    
    return session.access_token;
  };

  return (
    <AuthContext.Provider value={{ user, loading, accessToken, signIn, signOut, getAccessToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // Return default values for standalone usage
    // This can happen during hot module replacement or initial mount
    return {
      user: null,
      loading: false,
      accessToken: null,
      signIn: async () => {
        console.warn('[AuthContext] signIn called before AuthProvider is ready');
      },
      signOut: async () => {
        console.warn('[AuthContext] signOut called before AuthProvider is ready');
      },
      getAccessToken: async () => {
        console.warn('[AuthContext] getAccessToken called before AuthProvider is ready');
        return null;
      },
    };
  }
  return context;
}